<script>
    import { metaInfo } from "@/core/MetaInfo";
    import { validationService } from "@/core/ValidationService";
    import { cloudPayments } from "@/utils";
    import GiftTemplate from "@madrobots/components/gift/GiftTemplate";
    import GiftInfo from "@madrobots/components/gift/GiftInfo";
    import GiftDate from "@madrobots/components/gift/GiftDate";
    import GiftOrder from "@madrobots/components/gift/GiftOrder";

    const { required, email } = validationService.validators;

    export default {
        name: "Gift",
        components: {
            GiftTemplate,
            GiftInfo,
            GiftDate,
            GiftOrder,
        },
        data() {
            return {
                isLoadingPayment: false,
                isErrorPaymentResponse: false,
                templates: [],
                certificate: {
                    amount: "1000",
                    templateId: 1,
                },
                formInfo: {
                    sender: "",
                    recipient: "",
                    text: "",
                },
                formDate: {
                    senderEmail: "",
                    recipientEmail: "",
                    recipientPhone: "",
                    date: "",
                    reportDelivery: false,
                },
                deliveryType: "email",
                errors: {
                    formInfo: {
                        sender: "",
                        recipient: "",
                    },
                    formDate: {
                        senderEmail: "",
                        recipientEmail: "",
                        recipientPhone: "",
                    },
                },
            };
        },
        computed: {
            isValidFormInfo() {
                return Object.values(this.errors.formInfo).every((v) => v.length === 0);
            },
            isValidFormDate() {
                return Object.values(this.errors.formDate).every((v) => v.length === 0);
            },
            activeTemplate() {
                return this.templates.find((template) => {
                    return template.id === this.certificate.templateId;
                });
            },
        },
        mounted() {
            this.$store.giftCardApi.getTemplates().then((response) => {
                this.templates = response;
            });
        },
        beforeDestroy() {
            cloudPayments.destroy();
        },
        methods: {
            onSelectAmount(amount) {
                this.certificate.amount = amount;
            },
            onSelectTemplate(id) {
                this.certificate.templateId = id;
            },
            onUpdateInfoForm({ sender, recipient, text }) {
                this.formInfo = { sender, recipient, text };
            },
            onUpdateDateForm({
                senderEmail,
                recipientEmail,
                recipientPhone,
                date,
                reportDelivery,
            }) {
                this.formDate = {
                    senderEmail,
                    recipientEmail,
                    recipientPhone,
                    date,
                    reportDelivery,
                };
            },
            onBeforeSubmitValidation() {
                this.errors.formInfo.sender = required(this.formInfo.sender);
                this.errors.formInfo.recipient = required(this.formInfo.recipient);

                this.errors.formDate.senderEmail = email(this.formDate.senderEmail);
                if (this.deliveryType === "email") {
                    this.errors.formDate.recipientEmail = email(this.formDate.recipientEmail);
                }
                if (this.deliveryType === "sms") {
                    this.errors.formDate.recipientPhone = required(
                        this.formDate.recipientPhone
                    );
                }
            },
            scrollTo(selector) {
                window.scrollTo({
                    top:
                        document.querySelector(selector).getBoundingClientRect().top +
                        window.scrollY -
                        70,
                    behavior: "smooth",
                });
            },
            onSubmit() {
                if (this.isErrorPaymentResponse) {
                    cloudPayments.open();
                    return;
                }

                this.onBeforeSubmitValidation();
                if (!this.isValidFormInfo) {
                    this.scrollTo("#gift-info");
                    return;
                }

                if (!this.isValidFormDate) {
                    this.scrollTo("#gift-date");
                    return;
                }

                this.isLoadingPayment = true;

                this.$store.giftCardApi
                    .buy({
                        sender: this.formInfo.sender,
                        recipient: this.formInfo.recipient,
                        senderEmail: this.formDate.senderEmail,
                        recipientEmail: this.formDate.recipientEmail,
                        recipientPhone: this.formDate.recipientPhone,
                        congratulations: this.formInfo.text,
                        deliveryType: this.deliveryType.toUpperCase(),
                        deliveryDate: this.formDate.date,
                        amount: this.certificate.amount,
                        templateId: this.certificate.templateId,
                        reportDelivery: this.formDate.reportDelivery,
                    })
                    .then((response) => {
                        if (response.success) {
                            const { publicId, amount, currency, email, invoiceId, accountId } =
                                response.payment;

                            cloudPayments
                                .create({
                                    publicId,
                                    amount,
                                    currency,
                                    email,
                                    invoiceId,
                                    accountId,
                                    description: `Оплата подарочной карты GC${response.orderId}`,
                                })
                                .then(() => {
                                    this.isLoadingPayment = false;
                                    this.isErrorPaymentResponse = false;

                                    this.$popup.show("modal-alert", {
                                        title: "Спасибо за оформление сертификата!",
                                        text: `Номер вашего заказа <b>GC${response.orderId}</b> <br> Сертификат успешно оплачен и будет отправлен получателю.`,
                                    });
                                })
                                .catch(() => {
                                    this.isLoadingPayment = false;
                                    this.isErrorPaymentResponse = true;

                                    this.$popup.show("modal-alert", {
                                        title: "Произошла ошибка платежа!",
                                        text: "Ваш платеж по какой-то причине не был проведен. Попробуйте повторить платеж.",
                                    });
                                });
                        }
                    });
            },
            onResetError({ type, name }) {
                this.errors[type][name] = "";
            },
            onChangeDeliveryType(type) {
                this.deliveryType = type;
                this.errors.formDate.recipientEmail = "";
                this.errors.formDate.recipientPhone = "";
            },
        },
        metaInfo() {
            return metaInfo.getMeta("gift");
        },
    };
</script>

<template>
    <div class="gift-page">
        <div class="l-container">
            <h1 class="gift-page__title page-title">
                Подарочный электронный <br />
                сертификат Madrobots
            </h1>
            <div class="gift-page__subtitle">Для оплаты любых покупок в наших магазинах</div>

            <div class="gift-page__section">
                <gift-template
                    :amount="certificate.amount"
                    :templates="templates"
                    :active-template="activeTemplate"
                    @on-select-amount="onSelectAmount"
                    @on-select-template="onSelectTemplate"
                ></gift-template>
            </div>

            <div class="gift-page__section">
                <gift-info
                    :errors="errors.formInfo"
                    @on-update="onUpdateInfoForm"
                    @on-reset-error="onResetError"
                ></gift-info>
            </div>

            <div class="gift-page__section">
                <gift-date
                    :errors="errors.formDate"
                    @on-update="onUpdateDateForm"
                    @on-reset-error="onResetError"
                    @on-change-type="onChangeDeliveryType"
                ></gift-date>
            </div>

            <div class="gift-page__section">
                <gift-order
                    :certificate="certificate"
                    :form="formDate"
                    :active-template="activeTemplate"
                    :is-loading="isLoadingPayment"
                    :button-text="
                        isErrorPaymentResponse ? 'Повторить платеж' : 'Перейти к оплате'
                    "
                    @on-submit="onSubmit"
                ></gift-order>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    .gift-page {
        padding: 24px 0 60px;
        background-color: #e3e9f0;
        @include bp($bp-desktop-sm) {
            padding-top: 56px;
        }
    }

    .gift-page__title {
        margin-bottom: 10px;
        @include bp($bp-desktop-sm) {
            margin-bottom: 20px;
            text-align: center;
        }
    }

    .gift-page__subtitle {
        margin-bottom: 30px;
        font-size: 16px;
        font-weight: 500;
        line-height: 24px;
        text-align: center;
        @include bp($bp-desktop-sm) {
            margin-bottom: 60px;
            font-size: 20px;
            line-height: 28px;
        }
    }

    .gift-page__section {
        margin: 0 -10px 20px;
        @include bp($bp-desktop-sm) {
            margin: 0 0 30px;
        }
    }

    .gift-section {
        padding: 20px;
        background-color: #f0f4f9;
        border-radius: 24px;
        box-shadow: 0 2px 10px rgba(66, 71, 99, 0.1);
        @include bp($bp-desktop-sm) {
            padding: 30px;
            border-radius: 40px;
            box-shadow: none;
        }
        @include bp($bp-desktop-lg) {
            padding: 40px 60px;
        }
    }

    .gift-section__title {
        margin-bottom: 5px;
        font-size: 24px;
        font-weight: 700;
        line-height: 32px;
        @include bp($bp-desktop-sm) {
            margin-bottom: 10px;
            font-size: 36px;
            line-height: 44px;
        }
    }

    .gift-section__text {
        margin-bottom: 15px;
        font-size: 14px;
        line-height: 22px;
        @include bp($bp-desktop-sm) {
            margin-bottom: 30px;
            font-size: 18px;
            line-height: 28px;
        }
    }
</style>
